import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button';
import Textfield from '@atlaskit/textfield';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import { token } from '@atlaskit/tokens';

export type SpecificAccessFilterProps = {
	setFilterText: (textValue: string) => void;
	filterText: string;
	countOfIndividuals: number;
	countOfTeams: number;
	countOfGroups: number;
};

const styles = cssMap({
	textField: { marginBottom: token('space.400') },
});

const i18n = defineMessages({
	clearFilterLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.clear-filter',
		defaultMessage: 'Clear selection',
		description: 'Label for button that clears text from the filter field',
	},
	countOfIndividualsSuffix: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-groups-and-individuals-suffix',
		defaultMessage: 'and {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description:
			'Latter part of placeholder text when there are both individuals and groups, to be concatenated with share-and-restrict-dialog.specific-access-dialog.count-of-groups',
	},
	countOfGroups: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-groups',
		defaultMessage: 'Search {countOfGroups, plural, one {# group} other {# groups}}',
		description: 'Placeholder text when there are only groups',
	},
	countOfIndividuals: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-individuals',
		defaultMessage: 'Search {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description: 'Placeholder text when there are only individuals',
	},
	countOfTeamsMiddleClause: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams-middle-clause',
		defaultMessage: '{countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the content',
	},
	countOfTeams: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams',
		defaultMessage: 'Search {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are only teams in the content',
	},
	countOfTeamsSuffix: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams-suffix',
		defaultMessage: 'and {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the content',
	},
});

export const SpecificAccessFilter = ({
	setFilterText,
	filterText,
	countOfIndividuals,
	countOfGroups,
	countOfTeams,
}: SpecificAccessFilterProps) => {
	const intl = useIntl();
	const handleFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterText(event.currentTarget.value);
	};

	let placeholder;
	if (countOfIndividuals && countOfGroups && countOfTeams) {
		placeholder = `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})}, ${intl.formatMessage(i18n.countOfTeamsMiddleClause, { countOfTeams })}, ${intl.formatMessage(
			i18n.countOfIndividualsSuffix,
			{
				countOfIndividuals,
			},
		)}`;
	} else if (countOfGroups && countOfIndividuals) {
		placeholder = `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
			countOfIndividuals,
		})}`;
	} else if (countOfIndividuals && countOfTeams) {
		placeholder = `${intl.formatMessage(i18n.countOfTeams, {
			countOfTeams,
		})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
			countOfIndividuals,
		})}`;
	} else if (countOfTeams && countOfGroups) {
		placeholder = `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})} ${intl.formatMessage(i18n.countOfTeamsSuffix, {
			countOfTeams,
		})}`;
	} else if (countOfTeams) {
		placeholder = intl.formatMessage(i18n.countOfTeams, {
			countOfTeams,
		});
	} else if (countOfGroups) {
		placeholder = intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		});
	} else {
		placeholder = intl.formatMessage(i18n.countOfIndividuals, {
			countOfIndividuals,
		});
	}

	return (
		<Textfield
			aria-label={placeholder}
			css={styles.textField}
			autoFocus
			placeholder={placeholder}
			value={filterText}
			onChange={handleFilterTextChange}
			elemAfterInput={
				<>
					{Boolean(filterText) && (
						<Button
							appearance="subtle"
							aria-label={intl.formatMessage(i18n.clearFilterLabel)}
							iconBefore={<SelectClearIcon label="" color={token('color.icon.subtle')} />}
							onClick={() => setFilterText('')}
						/>
					)}
				</>
			}
		/>
	);
};
